export const USER_TYPES = {
  ADMIN: "admin", // System Administrator
  CEED: "manager",
  INDIVIDUAL: "individual", // Individual User
  OWNER: "owner", // Company User (Owner/Admin)
  INVITED: "invited", // Invited User (member/viewer)
  STAFF: "staff",
};

/**
 * UX role resolver.
 * We DO NOT rely on user.role (which is only "admin" | "user").
 * Instead we use:
 *   - user.role === "admin"  -> ADMIN
 *   - if user.accountType === "organization":
 *       - primaryOrgRole in ["owner","admin"]  -> OWNER
 *       - primaryOrgRole in ["member","viewer"]-> INVITED
 *   - else -> INDIVIDUAL
 */
export function getUserType(user) {
  if (!user) return null;

  if (user.role === "admin") return USER_TYPES.ADMIN;
  if (user.role === "manager") return USER_TYPES.CEED; // ⬅️ NEW

  if (user.accountType === "organization") {
    const r = (user.primaryOrgRole || "").toLowerCase();
    if (r === "owner") return USER_TYPES.OWNER;
    if (r === "staff") return USER_TYPES.STAFF;
    if (r === "member" || r === "viewer") return USER_TYPES.INVITED;
    // Fallback: if we know it's an org account but no role came through,
    // default to OWNER (safer for company creator). Adjust if you prefer INVITED.
    return USER_TYPES.OWNER;
  }

  return USER_TYPES.INDIVIDUAL;
}

export const ABILITIES = {
  [USER_TYPES.ADMIN]: new Set([
    "admin.dashboard",
    "admin.users",
    "admin.projects",
    "admin.furnitures",
    "admin.assets",
    "admin.subscriptions",
    "admin.purchaseplans",
    "admin.reports",
    "admin.quotes",
    "admin.security",
    "admin.revit",
    "admin.editorsettings",
  ]),
  [USER_TYPES.CEED]: new Set([
    "ceed.dashboard",
    "ceed.projects",
    "ceed.furnitures",
    "ceed.assets",
    "ceed.subscriptions",
    "ceed.purchaseplans",
    "ceed.quotes",
    "ceed.reports",
    "ceed.notifications",
    "ceed.profile",
  ]),
  [USER_TYPES.INDIVIDUAL]: new Set([
    "app.dashboard",
    "app.projects",
    "app.quotes",
    "app.estimator",
    "app.subscription",
    "app.support",
    "app.engineeringplans",
  ]),

  [USER_TYPES.OWNER]: new Set([
    "app.dashboard",
    "app.projects",
    "app.staff",
    "app.orgquotes",
    "app.pricing",
    "app.branding.edit",
    "app.subscription",
    "app.support",
    "app.autoestimator",
    "app.engineeringplans",
  ]),
  [USER_TYPES.STAFF]: new Set([
    "app.dashboard",
    "app.projects",
    "app.orgquotes",
    "app.pricing",
    "app.branding.edit",
    "app.support",
    "app.subscription",
    "app.autoestimator",
    "app.engineeringplans",
  ]),

  [USER_TYPES.INVITED]: new Set([
    "app.dashboard",
    "app.projects",
    "app.myquotes",
    "app.support",
    "app.engineeringplans",
  ]),
};

export const CEED_ALWAYS_OPEN = [
  "ceed.dashboard", // dashboard landing
  "ceed.projects",
  "ceed.notifications",
  "ceed.profile",
];

export const STAFF_ALWAYS_OPEN = [
  "app.dashboard",
  "app.projects",
  "app.notifications",
  "app.profile",
  "app.support",
  "app.engineeringplans",
];

export function can(user, perm) {
  if (!perm) return true; // routes without a perm are open
  if (!user) return false;
  // platform admins can see everything
  if (user.role === "admin") return true;

  // Manager gating: allow if in ALWAYS_OPEN, else check allow/deny arrays
  if (user.role === "manager") {
    if (CEED_ALWAYS_OPEN.includes(perm)) return true;
    const allow = Array.isArray(user.permsAllow) ? user.permsAllow : [];
    const deny = Array.isArray(user.permsDeny) ? user.permsDeny : [];
    if (deny.includes(perm)) return false;
    if (allow.includes(perm)) return true;
    return false; // default-closed
  }
  if (user.role === "user" && user.accountType === "individual") return true;
  // Company STAFF: default-closed except always-open + org allow list
  if (
    user.accountType === "organization" &&
    (user.primaryOrgRole || "").toLowerCase() === "staff"
  ) {
    if (STAFF_ALWAYS_OPEN.includes(perm)) return true;
    const allow = Array.isArray(user.permsAllow) ? user.permsAllow : [];
    const deny = Array.isArray(user.permsDeny) ? user.permsDeny : [];
    if (deny.includes(perm)) return false;
    if (allow.includes(perm)) return true;
    return false;
  }

  // Company OWNER (or org admin): open company features by default
  if (
    user.accountType === "organization" &&
    ["owner", "admin"].includes((user.primaryOrgRole || "").toLowerCase())
  ) {
    return true;
  }
  if (
    user.accountType === "organization" &&
    ["member"].includes((user.primaryOrgRole || "").toLowerCase())
  ) {
    return true;
  }
  // Individual / Invited members: allow the app-level always-open
  if (perm.startsWith("app.")) {
    // Individual default-open pages (same as STAFF_ALWAYS_OPEN)
    if (
      [
        "app.dashboard",
        "app.projects",
        "app.notifications",
        "app.profile",
      ].includes(perm)
    )
      return true;
  }
  // other roles — keep your existing logic (examples below)
  // owners/staff/members may use your current feature flags or plan checks
  if (user?.features && user.features[perm]) return true;
  return false;
}
